// Imports for external libraries go here.
import React, { FC } from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { Button, Icon, Text, Label, Image, Types, Badge } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { GiftCardDetails } from '../../modules/store/giftCardStore';
import { constants, localeDateFormats, convertDateToLocaleSpecific, GIFT_CARD_STATUS } from '../../modules';
import { getFallbackImage } from '../../modules/utils';
import { getGiftCardNumberDisplayFormat } from '../../organisms/ProfileSection/ProfileUtils/profileHelper';
import { GiftCardsListProps } from './GiftCardsList.types';
import { StyledGiftCardsList } from './GiftCardsList.styles';
import { formatGiftCardCurrency } from '../GiftCard/__helpers__';

// Build a per-row aria-label for the View CTA: "<aem template> <masked card #>".
// Falls back to the plain label, then the masked number, so SR users always
// hear which row they activated.
const buildViewCtaAriaLabel = (
  ariaLabelTemplate: string | undefined,
  fallbackLabel: string | undefined,
  numberDisplay: string
): string => {
  const base = ariaLabelTemplate || fallbackLabel || '';
  if (!base) return numberDisplay;
  return numberDisplay ? `${base} ${numberDisplay}` : base;
};

// Use named rather than default exports.
export const GiftCardsList: FC<GiftCardsListProps> = props => {
  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { ACCEPT_LANGUAGE: currentLocale } = clientEnvVars;

  const RenderGiftCardStatus = ({
    cardDetails,
    customClass,
  }: {
    cardDetails: GiftCardDetails;
    customClass?: string;
  }) => {
    return (
      <div className={clsx(customClass)}>
        {cardDetails.cardStatus === GIFT_CARD_STATUS.ON_HOLD && cardDetails?.hasHoldNotLost && (
          <Badge
            badgeVariation={Types.BadgeVariation?.Overlay}
            badgeText={props?.lockedLabel?.toUpperCase() ?? ''}
            badgeIcon={props?.lockedIcon}
            isInlineMerch={false}
            customClass={clsx(
              'min-width-max-content align-self-start',
              props?.isAccountRefreshWalletWidget ? 'wallet-badge-neutral' : 'm-badge-overlay-neutral p-2'
            )}
          />
        )}
        {cardDetails?.cardStatus === GIFT_CARD_STATUS.ON_HOLD && !cardDetails?.hasHoldNotLost && (
          <Badge
            badgeVariation={Types.BadgeVariation?.Overlay}
            badgeText={props?.onHoldLabel?.toUpperCase() ?? ''}
            badgeIcon={props?.onHoldIcon}
            isInlineMerch={false}
            customClass={clsx(
              'min-width-max-content align-self-start',
              props?.isAccountRefreshWalletWidget ? 'wallet-badge-neutral' : 'm-badge-overlay-neutral p-2'
            )}
          />
        )}
        {cardDetails?.createTimestamp && moment().diff(moment(cardDetails?.createTimestamp), 'days') < 14 && (
          <Badge
            badgeVariation={Types.BadgeVariation?.Overlay}
            badgeText={props?.newCardLabel?.toUpperCase() ?? ''}
            badgeIcon={props?.newCardIcon}
            isInlineMerch={true}
            customClass={clsx(
              'min-width-max-content align-self-start',
              props?.isAccountRefreshWalletWidget ? 'wallet-badge-merch' : 'p-2'
            )}
          />
        )}
      </div>
    );
  };

  const RenderGiftCardExpiration = ({ cardDetails }: { cardDetails: GiftCardDetails }) => {
    // Use API's cardStatus as source of truth for expired status, consistent with Card modal behavior
    const isExpired = cardDetails?.cardStatus === GIFT_CARD_STATUS.EXPIRED;
    const isCardExpiringSoon =
      cardDetails.certificateExpirationDate &&
      !isExpired &&
      moment(cardDetails?.certificateExpirationDate).diff(moment(), 'days') <= constants.EXPIRING_SOON_DAYS;

    const getCardStatusClasses = (isExpiringSoon: boolean, isExpired: boolean) => ({
      iconClass: isExpiringSoon ? 'card-expiring-icon' : isExpired ? 'card-expired-icon' : '',
      labelClass: isExpiringSoon ? 'card-expiring-label' : isExpired ? 'card-expired-label' : '',
      label: isExpiringSoon ? props.expiringSoonLabel : isExpired ? props?.expiredLabel : '',
    });

    const statusClasses = getCardStatusClasses(Boolean(isCardExpiringSoon), isExpired);

    // Get locale-specific date format with proper fallback chain
    const localeKey = currentLocale?.replace('_', '-');
    const numericDateFormat =
      (localeKey && localeDateFormats[localeKey]?.numericDate) ??
      localeDateFormats['default']?.numericDate ??
      'MM/DD/YYYY';
    const formattedExpirationDate = convertDateToLocaleSpecific(
      cardDetails?.certificateExpirationDate || undefined,
      currentLocale || undefined,
      numericDateFormat
    );

    return cardDetails?.certificateExpirationDate ? (
      props?.isAccountRefreshWalletWidget ? (
        // Wallet widget: expiring soon on separate line
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center gift-card-expiry-row">
            <Text
              element={Types.tags.span}
              fontSize={Types.size.medium}
              copyText={`${props?.expiresLabel ? `${props.expiresLabel}: ` : ''}${formattedExpirationDate}`}
              customClass="t-font-m gift-card-expiry"
            />
          </div>
          {statusClasses?.label && statusClasses?.iconClass && (
            <div className="d-flex align-items-center mt-1 mt-md-0">
              <Icon iconClass={clsx(props?.expiringSoonIcon || 'icon-information', 'mr-2', statusClasses?.iconClass)} />
              <Text
                customClass={clsx('t-font-xs', statusClasses?.labelClass)}
                element={Types.tags.span}
                fontSize={Types.size.extraSmall}
                copyText={statusClasses?.label}
              />
            </div>
          )}
        </div>
      ) : (
        // BAU: expiring soon inline on same line
        <div className="d-flex flex-column mt-1 mt-md-0 flex-md-row align-items-start align-items-md-center">
          <span className="mx-1 text-muted gift-card-separator d-none d-md-block t-font-s">|</span>
          <Text
            element={Types.tags.span}
            fontSize={Types.size.medium}
            copyText={`${props?.expiresLabel ? `${props.expiresLabel}: ` : ''}${formattedExpirationDate}`}
            customClass="t-font-m gift-card-expiry"
          />
          {statusClasses?.label && statusClasses?.iconClass && (
            <div className="d-flex align-items-center ml-md-2 mt-1 mt-md-0">
              <Icon iconClass={clsx(props?.expiringSoonIcon || 'icon-information', 'mr-2', statusClasses?.iconClass)} />
              <Text
                customClass={clsx('t-font-xs', statusClasses?.labelClass)}
                element={Types.tags.span}
                fontSize={Types.size.extraSmall}
                copyText={statusClasses?.label}
              />
            </div>
          )}
        </div>
      )
    ) : null;
  };

  const RenderGiftCardViewCta = ({ cardDetails, index }: { cardDetails: GiftCardDetails; index?: number }) => {
    if (!props?.viewCardCtaLabel) return null;
    const numberDisplay = getGiftCardNumberDisplayFormat(cardDetails?.cardISO, cardDetails?.serialNumber);
    const ariaLabel = buildViewCtaAriaLabel(props?.viewCardCtaAriaLabel, props?.viewCardCtaLabel, numberDisplay);
    return (
      <Button
        isLink={false}
        type={Types.ButtonTypeVariation.Button}
        className="m-link-action gift-card-view-cta p-0"
        callback={() => props?.onViewCard?.(cardDetails)}
        buttonCopy={props.viewCardCtaLabel}
        ariaLabel={ariaLabel}
        testId={typeof index === 'number' ? props?.viewCtaTestId?.(index) : undefined}
        trackingProperties={props?.viewCardTracking}
        custom_click_track_value={props?.viewCardCustomClickTrackValue}
      />
    );
  };

  const RenderGiftCardDetails = ({ cardDetails, index }: { cardDetails: GiftCardDetails; index?: number }) => {
    const { symbol, value, currencyCode } = formatGiftCardCurrency(
      cardDetails?.certificateBalance,
      cardDetails?.cardCurrency,
      currentLocale
    );
    return props?.isAccountRefreshWalletWidget ? (
      <div className="d-flex flex-row align-items-start gift-card-details ml-3 justify-content-between">
        <div className="d-flex flex-column flex-grow-1 min-width-0">
          <div className="d-flex flex-row align-items-center flex-wrap gift-card-amount-row">
            <span className="t-subtitle-l gift-card-amount">{`${symbol}${value} ${currencyCode}`}</span>
            <RenderGiftCardStatus
              cardDetails={cardDetails}
              customClass="gift-card-status-badges ml-2 d-none d-md-flex"
            />
          </div>
          <div className="d-flex flex-row align-items-center flex-wrap mt-1 gift-card-number-row">
            <span className="t-font-s gift-card-number">
              {getGiftCardNumberDisplayFormat(cardDetails?.cardISO, cardDetails?.serialNumber)}
            </span>
          </div>
          <RenderGiftCardExpiration cardDetails={cardDetails} />
          <RenderGiftCardStatus cardDetails={cardDetails} customClass="gift-card-status-badges d-flex d-md-none mt-2" />
        </div>
        <div className="d-flex flex-row align-items-center gift-card-trailing">
          <RenderGiftCardViewCta cardDetails={cardDetails} index={index} />
        </div>
      </div>
    ) : (
      <div className="d-flex flex-column flex-md-row align-items-start gift-card-details ml-3 justify-content-start">
        <div className="d-flex flex-column mr-0 mr-md-4 mb-3 mb-md-0">
          <div className="d-flex flex-row align-items-center">
            <Text
              element={Types.tags.span}
              fontSize={Types.size.small}
              copyText={getGiftCardNumberDisplayFormat(cardDetails?.cardISO, cardDetails?.serialNumber)}
              customClass="t-font-s"
            />
            <RenderGiftCardStatus cardDetails={cardDetails} customClass="d-none d-md-block ml-4" />
          </div>

          <div className="d-flex flex-column mt-1 flex-md-row align-items-start align-items-md-center">
            <Label
              customClass="gift-card-balance"
              labelText={`${props?.balanceLabel ? `${props.balanceLabel}: ` : ''}${symbol}${value} ${currencyCode}`}
              size={Types.size.small}
            />
            <RenderGiftCardExpiration cardDetails={cardDetails} />
          </div>
        </div>
        <RenderGiftCardStatus cardDetails={cardDetails} customClass="d-sm-block d-md-none" />
      </div>
    );
  };

  return (
    <StyledGiftCardsList
      className={clsx(props?.isAccountRefreshWalletWidget && 'wallet-widget-variant')}
      data-testid="account-GiftCardsList"
      data-component-name="m-account-GiftCardsList"
    >
      {props?.giftCards?.map((cardDetail: GiftCardDetails, index: number) => {
        const numberDisplay = getGiftCardNumberDisplayFormat(cardDetail?.cardISO, cardDetail?.serialNumber);
        const { symbol, value, currencyCode } = formatGiftCardCurrency(
          cardDetail?.certificateBalance,
          cardDetail?.cardCurrency,
          currentLocale
        );
        const rowAriaLabel = [symbol && value ? `${symbol}${value} ${currencyCode}` : '', numberDisplay]
          .filter(Boolean)
          .join(' ');
        return (
          <div
            role="group"
            aria-label={rowAriaLabel || undefined}
            className={clsx(
              'd-flex flex-row',
              props?.isAccountRefreshWalletWidget ? 'align-items-center' : 'align-items-start',
              props?.isAccountRefreshWalletWidget
                ? index === props?.giftCards?.length - 1
                  ? 'pt-4'
                  : 'py-4 gift-card-wrapper'
                : ''
            )}
            key={index}
            data-testid={props?.rowTestId?.(index)}
          >
            <Image
              customClass={clsx(props?.isAccountRefreshWalletWidget ? 'gift-card-image' : 'gift-card-profile-image')}
              altText={props?.imageAltText ?? props?.featuredDynamicImage?.altText}
              renditions={props?.featuredDynamicImage?.renditions}
              dynamic={props?.featuredDynamicImage?.dynamic}
              defaultImageURL={
                props?.image ?? props?.featuredDynamicImage?.assetPath ?? getFallbackImage(currentLocale).Classic
              }
            />
            <RenderGiftCardDetails cardDetails={cardDetail} index={index} />
          </div>
        );
      })}
    </StyledGiftCardsList>
  );
};
